import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { isRecord } from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import type { OepTargetFileJob, OepTargetFileJobStatus } from '../types'

const POLL_INTERVAL_MS = 5000

/** Extract the HTTP status from an axios-shaped error, or null if not present. */
function getHttpStatus(error: unknown): number | null {
  const response =
    isRecord(error) && isRecord(error.response) ? error.response : null
  return isRecord(response) && typeof response.status === 'number'
    ? response.status
    : null
}

function isInFlightStatus(status: OepTargetFileJobStatus): boolean {
  return status === 'QUEUED' || status === 'PROCESSING'
}

export function isInFlightJob(
  job: OepTargetFileJob | null | undefined
): boolean {
  return job !== null && job !== undefined && isInFlightStatus(job.status)
}

export interface GeneratedWorkbookJobsMessages {
  readonly loadFailure: string
  readonly generateFailure: string
  readonly downloadFailure: string
  /** Toasted instead of the raw error when a generate request fails with that HTTP status. */
  readonly generateFailureByStatus: Readonly<Record<number, string>>
}

export interface UseGeneratedWorkbookJobsOptions {
  readonly queryKey: readonly unknown[]
  readonly fetchLatestJob: () => Promise<OepTargetFileJob | null>
  readonly generateJob: (documentId?: string) => Promise<OepTargetFileJob>
  readonly fetchDownloadUrl: (
    jobId: string
  ) => Promise<{ readonly presignedUrl: string }>
  readonly messages: GeneratedWorkbookJobsMessages
  /** When false, skips the latest-job query (e.g. feature flag off). Default true. */
  readonly enabled?: boolean
}

export interface GeneratedWorkbookJobsResult {
  readonly latestJob: OepTargetFileJob | null
  readonly isLoading: boolean
  readonly isGenerating: boolean
  /** Document whose generate request is in flight, or null when none is. */
  readonly generatingDocumentId: string | null
  readonly isGenerateDisabled: boolean
  readonly handleGenerate: (documentId?: string) => Promise<void>
  readonly handleDownload: (jobId: string) => Promise<void>
}

/**
 * Latest-job polling, generate and download for one generated-workbook job
 * resource (ABP-4 target file, ABP-5 file). Every failure surfaces as a toast
 * — the handlers are called fire-and-forget and must never reject.
 */
export function useGeneratedWorkbookJobs({
  queryKey,
  fetchLatestJob,
  generateJob,
  fetchDownloadUrl,
  messages,
  enabled = true,
}: UseGeneratedWorkbookJobsOptions): GeneratedWorkbookJobsResult {
  const queryClient = useQueryClient()

  const {
    data: latestJob,
    isLoading,
    error: loadError,
  } = useQuery({
    queryKey,
    queryFn: fetchLatestJob,
    enabled,
    refetchInterval: (query) => {
      const job = query.state.data
      if (isInFlightJob(job)) {
        return POLL_INTERVAL_MS
      }
      return false
    },
  })

  // Toast once per load-error occurrence (not on every render/retry while it
  // persists), and reset the guard once the query recovers — mirrors the
  // documentErrorShown pattern in OpenEnrollmentApprovalContainer.
  const loadErrorShownRef = useRef(false)
  useEffect(() => {
    if (loadError !== null && !loadErrorShownRef.current) {
      loadErrorShownRef.current = true
      toast.error(messages.loadFailure)
    }
    if (loadError === null) {
      loadErrorShownRef.current = false
    }
  }, [loadError, messages.loadFailure])

  const generateMutation = useMutation({
    mutationFn: (documentId?: string) => generateJob(documentId),
    onSuccess: (job) => {
      queryClient.setQueryData(queryKey, job)
      void queryClient.invalidateQueries({ queryKey })
    },
  })

  const handleGenerate = useCallback(
    async (documentId?: string) => {
      try {
        await generateMutation.mutateAsync(documentId)
      } catch (err: unknown) {
        const status = getHttpStatus(err)
        const statusMessage =
          status !== null ? messages.generateFailureByStatus[status] : undefined
        if (statusMessage !== undefined) {
          toast.error(statusMessage)
          return
        }
        toast.error(
          err instanceof Error ? err.message : messages.generateFailure
        )
      }
    },
    [generateMutation, messages]
  )

  // A blocked pop-up is deliberately not reported: `window.open` returns null
  // whenever `noopener`/`noreferrer` is in the features string, even on
  // success, so its return value cannot tell the two apart. The admin's own
  // pop-up blocker telling them it blocked a tab is good enough.
  const handleDownload = useCallback(
    async (jobId: string) => {
      try {
        const { presignedUrl } = await fetchDownloadUrl(jobId)
        window.open(presignedUrl, '_blank', 'noopener,noreferrer')
      } catch (err: unknown) {
        toast.error(
          err instanceof Error ? err.message : messages.downloadFailure
        )
      }
    },
    [fetchDownloadUrl, messages.downloadFailure]
  )

  const resolvedJob = latestJob ?? null
  const isGenerateDisabled =
    generateMutation.isPending || isInFlightJob(resolvedJob)
  const generatingDocumentId =
    generateMutation.isPending &&
    typeof generateMutation.variables === 'string' &&
    generateMutation.variables.length > 0
      ? generateMutation.variables
      : null

  return {
    latestJob: resolvedJob,
    isLoading,
    isGenerating: generateMutation.isPending,
    generatingDocumentId,
    isGenerateDisabled,
    handleGenerate,
    handleDownload,
  }
}
